import { Component, inject, signal, ChangeDetectionStrategy } from "@angular/core";

import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { GateCatalogService } from "../../../../../services/api/gate-catalog/gate-catalog.service";
import { Make, MakeObject } from "../../../../../models/api/gateComponents/make";
import { first } from "rxjs";

@Component({
  standalone: true,
  selector: "app-make",
  imports: [ButtonComponent, ReactiveFormsModule, TranslateModule, InputTextModule],
  templateUrl: "./make.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./make.component.scss",
})
export class MakeComponent {
  private config = inject(DynamicDialogConfig);
  private ref = inject(DynamicDialogRef);

  private make: Make = this.config.data.gateCatalogItem;
  private actionType: "add" | "edit" = this.config.data.actionType;
  private gateCatalog = inject(GateCatalogService);

  public form = new FormGroup({
    label: new FormControl(this.make.label, Validators.required),
  });

  public requestStarted = signal(false);

  /**
   * When the user presses Enter, save the form
   * @param event Keyboard event
   */
  public onKeyUp(event: KeyboardEvent) {
    if (event.key === "Enter") {
      this.save();
    }
  }

  /**
   * Saves the Make form to the server, either creating a new one or editing an existing one.
   * If the save is successful, it will close the dialog with the updated Make as the result.
   */
  public save() {
    const makeValues = this.form?.value;

    if (this.form.valid && makeValues && makeValues.label) {
      if (!this.requestStarted()) {
        const make: MakeObject = {
          label: makeValues.label,
        };
        this.make.properties = make;
        this.requestStarted.set(true);
        switch (this.actionType) {
          case "add":
            this.gateCatalog
              .createGateCatalogItem(this.make)
              .pipe(first())
              .subscribe({
                next: () => {
                  this.requestStarted.set(false);
                  this.ref.close(this.make);
                },
                error: () => {
                  this.requestStarted.set(false);
                },
              });
            break;
          case "edit":
            this.gateCatalog
              .editGateCatalogItem(this.make)
              .pipe(first())
              .subscribe({
                next: () => {
                  this.requestStarted.set(false);
                  this.ref.close(this.make);
                },
                error: () => {
                  this.requestStarted.set(false);
                },
              });
            break;
        }
      }
    }
  }

  /**
   * Closes the dialog without returning any result.
   */
  public closeDialog() {
    this.ref.close();
  }
}
